import { ImageArrowRight, ImageX } from "components/utility/images";
import { setGALocation } from "./utility/setGALocation";
import { useRef } from "react";
import { audienceSortStepNames } from "./utility/audienceSortStepNames";
import {
	changeCountry,
	getAudienceSelection,
	eventMisfired,
	individualGUID,
	financialProfessionalGUID
} from "./utility/audienceSortApiHandlers";
import { AudienceSelectorNarrowProps } from "./utility/audienceSortPropTypes";

const AudienceSelectorNarrow: React.FC<AudienceSelectorNarrowProps> = ({
	setStep,
	audienceTypesData,
	setAudienceSelectionData,
	currentPage,
	setCountryListData,
	setModalJustOpened,
	countryListData,
	setIsModalOpen,
	audienceSortTheme
}) => {
	const audienceSortBackButton = useRef<HTMLDivElement>(null);

	const closeModal = (event: React.MouseEvent | React.KeyboardEvent) => {
		if (eventMisfired(event)) return;
		if (setIsModalOpen) setIsModalOpen(false);
		setStep(audienceSortStepNames.initialStep);
	};

	const handleAudienceSelection = async (
		event: React.MouseEvent | React.KeyboardEvent,
		item
	): Promise<void> => {
		if (eventMisfired(event)) return;
		await getAudienceSelection(
			event,
			item,
			null,
			audienceTypesData,
			setAudienceSelectionData,
			currentPage
		);
		setStep(
			item.audienceTypeId === individualGUID
				? audienceSortStepNames.initialStep
				: audienceSortStepNames.financialProfessionalAffirmation
		);
	};

	const handleAudienceSelectionButton = async (event, item) => {
		if (eventMisfired(event)) return;
		await handleAudienceSelection(event, item);

		if (item.audienceTypeId === individualGUID) {
			setIsModalOpen(false);
			return;
		}
		if (setModalJustOpened) setModalJustOpened(false);
	};

	const handleCountrySwitch = async (
		event: React.MouseEvent | React.KeyboardEvent
	): Promise<void> => {
		if (eventMisfired(event)) return;
		if (!countryListData) {
			await changeCountry(event, audienceTypesData, setCountryListData);
		}
		if (setModalJustOpened) setModalJustOpened(false);
		setStep(audienceSortStepNames.countrySelection);
	};

	return (
		<div className={"audience-selector-narrow " + audienceSortTheme}>
			<div
				className="audience-selector-narrow-intro"
				role="button"
				tabIndex={0}
				ref={audienceSortBackButton}
			>
				{audienceTypesData.userSelectionText && (
					<h3 className="audience-selector-narrow-intro-label">
						{audienceTypesData.userSelectionText}
					</h3>
				)}
				<div
					className="audience-selector-narrow-intro-close"
					tabIndex={0}
					role="button"
					onClick={closeModal}
					onKeyUp={closeModal}
				>
					<ImageX />
				</div>
			</div>

			<ul className="audience-selector-narrow-items">
				{audienceTypesData.sectionAudienceType.map((item) => {
					return (
						<li
							key={item.title}
							className={`audience-selector-narrow-items-item ${
								item.isActive && currentPage !== "unknown-splash" ? "active" : ""
							}`}
						>
							<button
								data-a="true"
								data-a-lbl={item.title}
								data-a-cat="Header"
								data-a-act="Audience Switcher"
								data-a-evt="Audience Switch"
								data-a-loc={setGALocation(currentPage)}
								data-a-text={item.title}
								data-a-comp="audience switcher modal"
								data-qa={
									(item.audienceTypeId === financialProfessionalGUID &&
										"audience-button-professional") ||
									(item.audienceTypeId === individualGUID && "audience-button-individual")
								}
								aria-label={item.title}
								tabIndex={0}
								onClick={(event) => handleAudienceSelectionButton(event, item)}
								onKeyDown={(event) => handleAudienceSelectionButton(event, item)}
							>
								<div className="audience-selector-narrow-items-item-title">{item.title}</div>
							</button>
						</li>
					);
				})}
			</ul>
			<div
				className="audience-selector-narrow-location"
				role="button"
				tabIndex={0}
				onKeyUp={handleCountrySwitch}
				onClick={handleCountrySwitch}
				data-qa="audience-link-locationchange"
				aria-label={audienceTypesData.countrySelection.title}
			>
				<p>{audienceTypesData.countrySelection.title}</p>
				<ImageArrowRight />
			</div>
		</div>
	);
};

export default AudienceSelectorNarrow;
